import { Link } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import Layout from "../components/Layout";
import LocationSelect from "../components/LocationSelect";
import ManualStockMovementForm from "./ManualStockMovementForm";
import StockMovementHistoryPanel from "./StockMovementHistoryPanel";
import ExcelInventoryImportPanel from "./ExcelInventoryImportPanel";
import NegativeStockPanel from "./NegativeStockPanel";
import StockAdjustmentApprovalPanel from "./StockAdjustmentApprovalPanel";
import AuditLogPanel from "./AuditLogPanel";
import StockMatrixPanel from "./StockMatrixPanel";
import PhysicalStockCountPanel from "./PhysicalStockCountPanel";
import OfficeTransferApprovalPanel from "./OfficeTransferApprovalPanel";
import OfficeDamageApprovalPanel from "./OfficeDamageApprovalPanel";
import TransferDiscrepancyControlPanel from "./TransferDiscrepancyControlPanel";
import "./InventoryOverviewPage.css";
import OfficeTransferPlanningPanel from "./OfficeTransferPlanningPanel";

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

function formatQty(value) {
  return Number(value || 0).toLocaleString();
}

function stockStatus(qty) {
  const n = Number(qty || 0);

  if (n <= 0) return "OUT";
  if (n <= 3) return "LOW";
  return "OK";
}

const TRANSFER_ERROR_ALERT_STATUSES = new Set([
  "OPEN",
  "NEED_OFFICE_REVIEW",
  "RETURN_IN_TRANSIT",
  "WRONG_SIZE_RETURN_TO_SENDER_APPROVED",
  "WRONG_SIZE_RETURN_TO_CENTER_APPROVED",
  "WRONG_SIZE_RETURN_TO_WAREHOUSE_APPROVED",
  "WRONG_SIZE_RETURN_IN_TRANSIT",
]);

function isTransferErrorAlertRow(row) {
  const status = String(row?.status || "").trim().toUpperCase();
  return TRANSFER_ERROR_ALERT_STATUSES.has(status);
}

function PaginationBar({
  totalRows,
  page,
  pageSize,
  setPage,
  setPageSize,
  label = "rows",
}) {
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const from = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalRows);

  return (
    <div className="inventoryPagination">
      <div>
        Showing <b>{from}</b> - <b>{to}</b> of <b>{totalRows}</b> {label}
      </div>

      <div className="inventoryPagination__controls">
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
        >
          <option value={25}>25 rows</option>
          <option value={50}>50 rows</option>
          <option value={100}>100 rows</option>
        </select>

        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
          Prev
        </button>

        <span>
          Page <b>{page}</b> / {totalPages}
        </span>

        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}

const moduleCards = [
  {
    key: "matrix",
    no: "01",
    title: "Stock Matrix",
    desc: "View shop and warehouse stock by size matrix.",
    badge: "Inventory",
  },
  {
    key: "stockBalance",
    no: "02",
    title: "Current Stock Balance",
    desc: "View stock balance by location and product.",
    badge: "Balance",
  },
  {
    key: "physicalCount",
    no: "03",
    title: "Physical Stock Count",
    desc: "Import, approve, and apply physical stock count.",
    badge: "Opening",
  },
  {
    key: "excelImport",
    no: "04",
    title: "Shop Inventory Sheet Update",
    desc: "Update shop or warehouse stock using Excel sheet.",
    badge: "Excel",
  },
  {
    key: "manualMovement",
    no: "05",
    title: "Manual Stock Movement",
    desc: "Create manual IN, OUT, or adjustment movement.",
    badge: "Manual",
  },
  {
    key: "movementHistory",
    no: "06",
    title: "Stock Movement History",
    desc: "Check full stock ledger movement history.",
    badge: "Ledger",
  },
  {
    key: "negativeStock",
    no: "07",
    title: "Negative Stock Report",
    desc: "Review negative stock rows and request correction.",
    badge: "Alert",
  },
  {
    key: "adjustApproval",
    no: "08",
    title: "Stock Adjustment Approval",
    desc: "Approve and apply stock correction requests.",
    badge: "Approve",
  },
  {
    key: "transferApproval",
    no: "09",
    title: "Office Transfer Planning",
    desc: "Excel upload / manual create ဖြင့် ဆိုင်အလိုက် warehouse ပို့မည့် transfer list သတ်မှတ်ရန်။",
    badge: "Transfer",
  },
  {
    key: "transferDiscrepancy",
    no: "13",
    title: "Transfer Error Control",
    desc: "Review wrong, extra, and missing item reports with approval actions.",
    badge: "Error",
  },
  {
    key: "damageApproval",
    no: "10",
    title: "Damage Approval",
    desc: "Review, approve, and receive damage requests.",
    badge: "Damage",
  },
  {
    key: "auditLog",
    no: "11",
    title: "Audit Logs",
    desc: "View inventory action audit trail.",
    badge: "Audit",
  },
  {
    key: "lowStock",
    no: "12",
    title: "Low Stock Items",
    desc: "View low stock items list.",
    badge: "Warning",
  },
];

export default function InventoryOverviewPage({ embedded = false }) {
  const [location, setLocation] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [productCode, setProductCode] = useState("");
  const [activeModule, setActiveModule] = useState("matrix");

  const [summary, setSummary] = useState({});
  const [lowStockRows, setLowStockRows] = useState([]);
  const [rows, setRows] = useState([]);

  const [stockPage, setStockPage] = useState(1);
  const [stockPageSize, setStockPageSize] = useState(25);

  const [lowPage, setLowPage] = useState(1);
  const [lowPageSize, setLowPageSize] = useState(25);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [transferErrorCount, setTransferErrorCount] = useState(0);

  async function loadInventory() {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();

      query.set("location", location);
      query.set("type", type);

      if (productCode.trim()) {
        query.set("productCode", productCode.trim());
      }

      const [summaryRes, balanceRes] = await Promise.all([
        fetch(`${API_BASE}/api/erp/stock-summary`),
        fetch(`${API_BASE}/api/erp/stock-balance?${query.toString()}`),
      ]);

      const summaryData = await summaryRes.json();
      const balanceData = await balanceRes.json();

      if (!summaryRes.ok || summaryData.success === false) {
        throw new Error(summaryData.message || "Failed to load stock summary");
      }

      if (!balanceRes.ok || balanceData.success === false) {
        throw new Error(balanceData.message || "Failed to load stock balance");
      }

      setSummary(summaryData.summary || {});
      setLowStockRows(summaryData.lowStockRows || []);
      setRows(balanceData.rows || []);
      setStockPage(1);
      setLowPage(1);
      setError("");
    } catch (err) {
      const msg = err.message || "Inventory loading failed";

      if (rows.length === 0) {
        setError(msg);
      } else {
        console.warn("Inventory refresh warning:", msg);
        setError("");
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadTransferErrorCount() {
    try {
      const res = await fetch(
        `${API_BASE}/api/erp/office/transfer-error-alerts/count`
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        setTransferErrorCount(0);
        return;
      }

      setTransferErrorCount(Number(data.unreadCount || data.count || 0));
    } catch (err) {
      console.warn("Transfer error unread count load failed:", err);
      setTransferErrorCount(0);
    }
  }

  async function markTransferErrorCardViewed() {
    try {
      const res = await fetch(
        `${API_BASE}/api/erp/office/transfer-error-alerts/read`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            readBy: "OFFICE",
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        return;
      }

      setTransferErrorCount(0);
      setTimeout(loadTransferErrorCount, 400);
    } catch (err) {
      console.warn("Mark transfer error card viewed failed:", err);
    }
  }

  useEffect(() => {
    loadInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, type]);

  useEffect(() => {
    loadTransferErrorCount();

    const timer = setInterval(loadTransferErrorCount, 15000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (activeModule !== "transferDiscrepancy") return;

    markTransferErrorCardViewed();
  }, [activeModule]);

  const filteredRows = useMemo(() => {
    const keyword = productCode.trim().toUpperCase();

    if (!keyword) return rows;

    return rows.filter((row) =>
      String(row.product_code || "").toUpperCase().includes(keyword),
    );
  }, [rows, productCode]);

  const pagedStockRows = useMemo(() => {
    const start = (stockPage - 1) * stockPageSize;
    return filteredRows.slice(start, start + stockPageSize);
  }, [filteredRows, stockPage, stockPageSize]);

  const pagedLowStockRows = useMemo(() => {
    const start = (lowPage - 1) * lowPageSize;
    return lowStockRows.slice(start, start + lowPageSize);
  }, [lowStockRows, lowPage, lowPageSize]);

  function renderActiveModule() {
    if (activeModule === "matrix") {
      return <StockMatrixPanel />;
    }

    if (activeModule === "physicalCount") {
      return <PhysicalStockCountPanel onApplied={loadInventory} />;
    }

    if (activeModule === "excelImport") {
      return <ExcelInventoryImportPanel onImported={loadInventory} />;
    }

    if (activeModule === "manualMovement") {
      return <ManualStockMovementForm onSaved={loadInventory} />;
    }

    if (activeModule === "movementHistory") {
      return <StockMovementHistoryPanel />;
    }

    if (activeModule === "negativeStock") {
      return <NegativeStockPanel onRequestCreated={loadInventory} />;
    }

    if (activeModule === "adjustApproval") {
      return <StockAdjustmentApprovalPanel />;
    }

    if (activeModule === "auditLog") {
      return <AuditLogPanel />;
    }

    if (activeModule === "transferApproval") {
      return <OfficeTransferPlanningPanel />;
    }

    if (activeModule === "damageApproval") {
      return <OfficeDamageApprovalPanel />;
    }

    if (activeModule === "transferDiscrepancy") {
      return <TransferDiscrepancyControlPanel />;
    }

    if (activeModule === "lowStock") {
      return (
        <div className="inventoryPanel">
          <div className="inventoryPanel__head">
            <div>
              <span>Warning</span>
              <h2>Low Stock Items</h2>
            </div>
          </div>

          <PaginationBar
            totalRows={lowStockRows.length}
            page={lowPage}
            pageSize={lowPageSize}
            setPage={setLowPage}
            setPageSize={setLowPageSize}
            label="low stock rows"
          />

          <div className="inventoryTableWrap inventoryTableWrap--low">
            <table className="inventoryTable">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Color</th>
                  <th>Size</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Qty</th>
                </tr>
              </thead>

              <tbody>
                {pagedLowStockRows.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="inventoryEmpty">
                      No low stock items.
                    </td>
                  </tr>
                ) : (
                  pagedLowStockRows.map((row, index) => (
                    <tr key={`${row.product_code}-${row.location_code}-${index}`}>
                      <td>
                        <b>{row.product_code}</b>
                      </td>
                      <td>{row.color || "-"}</td>
                      <td>{row.size || "-"}</td>
                      <td>{row.location_code}</td>
                      <td>{row.location_type}</td>
                      <td>
                        <b>{formatQty(row.current_qty)}</b>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div className="inventoryPanel">
        <div className="inventoryPanel__head">
          <div>
            <span>Stock Balance</span>
            <h2>Current Stock by Location</h2>
          </div>

          <div className="inventoryQuickFilters">
            <button
              type="button"
              onClick={() => {
                setLocation("ALL");
                setType("SHOP");
                setStockPage(1);
              }}
            >
              All Shop Inventory
            </button>

            <button
              type="button"
              onClick={() => {
                setLocation("ALL");
                setType("WAREHOUSE");
                setStockPage(1);
              }}
            >
              All Warehouse Inventory
            </button>

            <button
              type="button"
              onClick={() => {
                setLocation("ALL");
                setType("ALL");
                setStockPage(1);
              }}
            >
              All Locations
            </button>
          </div>

          <div className="inventoryFilters">
            <LocationSelect
              value={location}
              onChange={setLocation}
              mode="all"
              includeAll
              allLabel="All Locations"
            />

            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="ALL">All Types</option>
              <option value="SHOP">Shop Only</option>
              <option value="WAREHOUSE">Warehouse Only</option>
            </select>

            <input
              value={productCode}
              onChange={(e) => setProductCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") loadInventory();
              }}
              placeholder="Search product code..."
            />

            <button onClick={loadInventory} disabled={loading}>
              Search
            </button>
          </div>
        </div>

        <PaginationBar
          totalRows={filteredRows.length}
          page={stockPage}
          pageSize={stockPageSize}
          setPage={setStockPage}
          setPageSize={setStockPageSize}
          label="stock rows"
        />

        <div className="inventoryTableWrap inventoryTableWrap--stock">
          <table className="inventoryTable">
            <thead>
              <tr>
                <th>Code</th>
                <th>Color</th>
                <th>Size</th>
                <th>Brand</th>
                <th>Location</th>
                <th>Type</th>
                <th>Qty</th>
                <th>Stock Value</th>
                <th>Last Movement</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {pagedStockRows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="inventoryEmpty">
                    No stock balance data yet.
                  </td>
                </tr>
              ) : (
                pagedStockRows.map((row, index) => {
                  const status = stockStatus(row.current_qty);

                  return (
                    <tr
                      key={`${row.product_code}-${row.color}-${row.size}-${row.location_code}-${index}`}
                    >
                      <td>
                        <b>{row.product_code}</b>
                      </td>
                      <td>{row.color || "-"}</td>
                      <td>{row.size || "-"}</td>
                      <td>{row.brand || "-"}</td>
                      <td>{row.location_code}</td>
                      <td>
                        <span
                          className={
                            row.location_type === "WAREHOUSE"
                              ? "inventoryType inventoryType--warehouse"
                              : "inventoryType inventoryType--shop"
                          }
                        >
                          {row.location_type}
                        </span>
                      </td>
                      <td>
                        <b>{formatQty(row.current_qty)}</b>
                      </td>
                      <td>{formatMoney(row.stock_value_mmk)} Ks</td>
                      <td>
                        {row.last_movement_time
                          ? new Date(row.last_movement_time).toLocaleString()
                          : "-"}
                      </td>
                      <td>
                        <span
                          className={
                            status === "OK"
                              ? "inventoryStatus inventoryStatus--ok"
                              : status === "LOW"
                                ? "inventoryStatus inventoryStatus--low"
                                : "inventoryStatus inventoryStatus--out"
                          }
                        >
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const activeCard = moduleCards.find((card) => card.key === activeModule);

  const content = (
    <div className="inventoryOverview">
      <div className="inventoryHero">
        <div>
          <span>Inventory Module</span>
          <h1>{embedded ? "Inventory Overview" : "Inventory Tools"}</h1>
          <p>
            Inventory tools are grouped into clean cards. Click a card to open only the related panel.
          </p>
        </div>

        <button onClick={loadInventory} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <div className="inventoryError">{error}</div>}

      <div className="inventoryCards">
        <div className="inventoryCard">
          <span>Stock Rows</span>
          <strong>{formatMoney(summary.stock_rows)}</strong>
        </div>

        <div className="inventoryCard inventoryCard--good">
          <span>Total Qty</span>
          <strong>{formatQty(summary.total_qty)}</strong>
        </div>

        <div className="inventoryCard">
          <span>Total Value</span>
          <strong>{formatMoney(summary.total_value)} Ks</strong>
        </div>

        <div className="inventoryCard">
          <span>Shop Qty</span>
          <strong>{formatQty(summary.shop_qty)}</strong>
        </div>

        <div className="inventoryCard inventoryCard--warehouse">
          <span>Warehouse Qty</span>
          <strong>{formatQty(summary.warehouse_qty)}</strong>
        </div>

        <div className="inventoryCard inventoryCard--warn">
          <span>Low Stock</span>
          <strong>{formatMoney(lowStockRows.length)}</strong>
        </div>
      </div>

      <div className="inventoryModuleGrid">
        {moduleCards.map((card) => (
          <button
            key={card.key}
            type="button"
            className={
              activeModule === card.key
                ? "inventoryModuleCard inventoryModuleCard--active"
                : "inventoryModuleCard"
            }
            onClick={() => setActiveModule(card.key)}
          >
            {card.key === "transferDiscrepancy" && transferErrorCount > 0 && (
              <span className="inventoryModuleCard__alertBall">
                +{transferErrorCount}
              </span>
            )}

            <div className="inventoryModuleCard__top">
              <span>{card.no}</span>
              <b>{card.badge}</b>
            </div>

            <h3>{card.title}</h3>
            <p>{card.desc}</p>

            <div className="inventoryModuleCard__open">
              Open
            </div>
          </button>
        ))}
      </div>

      <div className="inventoryActivePanel">
        <div className="inventoryActivePanel__title">
          <span>Selected Page</span>
          <h2>{activeCard ? activeCard.title : "Inventory Page"}</h2>
        </div>

        {renderActiveModule()}
      </div>
    </div>
  );

  if (embedded) return content;

  return <Layout title="Inventory Tools"><Link to="/erp/inventory">← All Shop Inventory</Link>{content}</Layout>;
}

// OFFICE_TRANSFER_PLANNING_CARD_V1

// USE_OFFICE_TRANSFER_PLANNING_PANEL_V1
